import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import LoadingCircle from 'components/ui/LoadingCircle';
import { getDriverPerformanceMetrics } from 'utils/MetricAPIUtils';
import { convertMetersToMiles } from 'utils/distance';
import { titleize } from 'underscore.string';
import { useTranslation } from 'react-i18next';
import { DEFAULT_DISTANCE_UNIT } from 'utils/localization/types';
import { Avatar } from '../modules/user/Avatar';
const TableColumnHeader = (props) => {
    const { label } = props;
    const tableHeaderClassNames = classnames({
        'px-6': 1,
        'py-3': 1,
        'text-left': 1,
        'text-xs': 1,
        'font-medium': 1,
        'text-white': 1,
        uppercase: 1,
        'tracking-wider': 1,
    });
    return (React.createElement("th", { scope: "col", className: tableHeaderClassNames }, label));
};
const MetricPercentageCell = (props) => {
    const { value } = props;
    return (React.createElement("td", { className: "px-6 py-4 whitespace-nowrap" },
        (value || value >= 0) && (React.createElement("span", { className: "text-grey-darkest" },
            value,
            "%")),
        Number.isNaN(value) && (React.createElement("div", { className: "flex items-center" },
            React.createElement(LoadingCircle, { text: "", color: "green" })))));
};
const MetricCell = (props) => {
    const { value } = props;
    return (React.createElement("td", { className: "px-6 py-4 whitespace-nowrap" },
        (value || value >= 0) && (React.createElement("span", { className: "text-grey-darkest" }, value)),
        Number.isNaN(value) && (React.createElement("div", { className: "flex items-center" },
            React.createElement(LoadingCircle, { text: "", color: "green" })))));
};
const DriverRow = (props) => {
    const { driver: { _id, first_name, last_name, username }, dateFilters, } = props;
    const [metrics, setMetrics] = useState({});
    const getMetric = (metricName, results) => {
        const metric = results.find((result) => result.metric === metricName);
        if (metric) {
            if (metricName === 'driverMileageVolume') {
                return convertMetersToMiles(metric.score);
            }
            return metric.score;
        }
        return 0;
    };
    useEffect(() => {
        const getMetrics = async () => {
            const { results } = await getDriverPerformanceMetrics(_id, dateFilters);
            const scores = {
                onTimePercentage: getMetric('onTime', results),
                podQualityPercentage: getMetric('podQuality', results),
                appUsagePercentage: getMetric('driverAppUsage', results),
                totalDistance: getMetric('driverMileageVolume', results),
                totalLoadCount: getMetric('driverLoad', results),
            };
            setMetrics(scores);
        };
        getMetrics();
    }, [props.driver, dateFilters]);
    const formatDistance = (miles) => {
        const distance = miles
            ? new Intl.NumberFormat('en-US').format(Math.round(metrics.totalDistance))
            : 0;
        return `${distance} ${DEFAULT_DISTANCE_UNIT}`;
    };
    return (React.createElement("tr", null,
        React.createElement("td", { className: "px-6 py-4 whitespace-nowrap" },
            React.createElement("div", { className: "flex items-center" },
                React.createElement("div", { className: "flex-shrink-0 h-10 w-10" },
                    React.createElement(Avatar, { key: _id, userId: _id, altName: "", className: "h-10 w-10 rounded-full" })),
                React.createElement("div", { className: "ml-4" },
                    React.createElement("div", { className: "text-sm font-medium text-gray-900" }, titleize(`${first_name} ${last_name}`)),
                    React.createElement("div", { className: "text-sm text-gray-500" }, `${username || '-'}`)))),
        React.createElement(MetricPercentageCell, { value: Math.round(metrics.onTimePercentage) }),
        React.createElement(MetricPercentageCell, { value: Math.round(metrics.podQualityPercentage) }),
        React.createElement(MetricPercentageCell, { value: Math.round(metrics.appUsagePercentage) }),
        React.createElement(MetricCell, { value: metrics.totalLoadCount }),
        React.createElement(MetricCell, { value: formatDistance(metrics.totalDistance) })));
};
const DriverMetricList = (props) => {
    const { drivers, dateFilters } = props;
    const { t } = useTranslation();
    return (React.createElement("div", { className: "flex flex-col" },
        React.createElement("div", { className: "-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8" },
            React.createElement("div", { className: "py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8" },
                React.createElement("div", { className: "shadow overflow-hidden border-b border-gray-200 sm:rounded-lg" },
                    React.createElement("table", { className: "min-w-full divide-y divide-gray-200" },
                        React.createElement("thead", { className: "bg-gray-700" },
                            React.createElement("tr", null,
                                React.createElement(TableColumnHeader, { label: t('NAME') }),
                                React.createElement(TableColumnHeader, { label: t('ON_TIME') }),
                                React.createElement(TableColumnHeader, { label: t('POD_QUALITY') }),
                                React.createElement(TableColumnHeader, { label: t('APP_USAGE') }),
                                React.createElement(TableColumnHeader, { label: t('LOADS') }),
                                React.createElement(TableColumnHeader, { label: t('DISTANCE') }))),
                        React.createElement("tbody", { className: "bg-white divide-y divide-gray-200" },
                            (!drivers || drivers.length === 0) && (React.createElement("tr", null,
                                React.createElement("td", { colSpan: 6, className: "p-6" },
                                    React.createElement("span", { className: "text-sm text-gray-900" },
                                        t('NO_DRIVER_DATA'),
                                        "...")))),
                            drivers &&
                                drivers.length > 0 &&
                                drivers.map((d) => {
                                    return (React.createElement(DriverRow, { key: d._id, driver: d, dateFilters: dateFilters }));
                                }))))))));
};
export default DriverMetricList;
